import { faLineColumns, faRotateLeft } from '@fortawesome/sharp-solid-svg-icons';
import { useCallback, useMemo, useState } from 'react';
import { useMeasure } from 'react-use';
import { Button } from '../stories/component';
import { SCAdvanceTable } from './advance_table';
import { ColumnManagerModal } from './column_manager_modal';
import type { BasicRecord, SCAdvanceTableInteractiveProps, TableColumn } from './types';
import { useAdvanceTableStateCache } from './use_table_state';

const SCAdvanceTableInteractiveInner = <D extends BasicRecord>(
  props: SCAdvanceTableInteractiveProps<D> & {
    tableState: ReturnType<typeof useAdvanceTableStateCache> | null;
  },
) => {
  const { tableState, ...restProps } = props;
  const [ref, size] = useMeasure();
  const [showColumnManager, setShowColumnManager] = useState(false);

  // Reorder columns based on persisted column order
  const orderedColumns = useMemo(() => {
    const columnOrder = tableState?.ColumnOrder as string[] | undefined;
    if (!columnOrder || columnOrder.length === 0) return props.Columns;
    const orderMap = new Map(columnOrder.map((fn: string, i: number) => [fn, i]));
    return [...props.Columns].sort((a: TableColumn, b: TableColumn) => {
      const ai = orderMap.has(a.FieldName) ? orderMap.get(a.FieldName)! : Infinity;
      const bi = orderMap.has(b.FieldName) ? orderMap.get(b.FieldName)! : Infinity;
      return ai - bi;
    });
  }, [props.Columns, tableState?.ColumnOrder]);

  // Ensure every column has an explicit Width for table-layout: fixed.
  // With width: 0 on the table, columns without a Width get 0px allocation.
  const DEFAULT_COLUMN_WIDTH = '150px';
  const columnsWithDefaults = useMemo(() => {
    return orderedColumns.map((col) =>
      col.Width ? col : { ...col, Width: col.MinWidth ?? DEFAULT_COLUMN_WIDTH },
    );
  }, [orderedColumns]);

  const handleColumnManagerApply = useCallback(
    (order: string[], visibleColumns: string[]) => {
      tableState?.SetColumnOrder?.(order);
      tableState?.SetColumns?.(visibleColumns);
    },
    [tableState],
  );

  const handleColumnManagerReset = useCallback(() => {
    tableState?.ResetColumnLayout?.();
    // Also reset visible columns to default
    tableState?.SetColumns?.(props.InitialTableState?.InitialColumns || []);
  }, [tableState, props.InitialTableState?.InitialColumns]);

  // Build interactive buttons (Columns, Reset) for the table container header
  // (same row as the search box), NOT the panel title bar.
  const tableHeaderButtons = useMemo(() => {
    const buttons: NonNullable<typeof props.HeaderButtons> = [];
    if (props.Id && props.ColumnReorderable !== false) {
      buttons.push({
        Label: 'Columns',
        Icon: faLineColumns,
        OnClick: () => setShowColumnManager((prev) => !prev),
      });
    }
    if (props.Id && (props.ColumnReorderable !== false || props.ColumnResizable !== false)) {
      buttons.push({
        Label: 'Reset columns',
        Icon: faRotateLeft,
        OnClick: () => {
          handleColumnManagerReset();
        },
      });
    }
    return buttons;
  }, [props.Id, props.ColumnReorderable, props.ColumnResizable, handleColumnManagerReset]);

  // Compute visible button count for CSS-based column width override.
  // eone-ui's internal formula (44 + (n-1)*43) overestimates; we pass the
  // count as a CSS custom property so a tighter width can be applied.
  const visibleButtonCount = useMemo(() => {
    if (!props.RowButtons || !props.Rows) return 0;
    // issue - if hidding buttons on a per row basis, the number of buttons visible at a given time will depend on the hidden field which is a string
    // need to loop through all rows, check which buttons are actually available and use that

    const buttonsHiddenBoolCount = props.RowButtons.filter(
      (btn) => (typeof btn.Hidden == 'boolean' && btn.Hidden !== true) || btn.Hidden == undefined,
    ).length;
    const buttonsHiddenStringFields = props.RowButtons.filter(
      (btn) => typeof btn.Hidden == 'string',
    ); // to hold the hidden fields that are applied on a per-row basis
    let buttonsHiddenStringCount = 0;

    buttonsHiddenStringFields.map((hiddenField) => {
      let show = false;
      for (let i = 0; i < props.Rows.length; i++) {
        const row = props.Rows[i];
        if (row[hiddenField.Hidden] == false) {
          show = true;
          break;
        }
      }
      if (show) {
        buttonsHiddenStringCount++;
      }
    });
    const allowedCount = buttonsHiddenBoolCount + buttonsHiddenStringCount;
    return allowedCount;
  }, [props.RowButtons, props.Rows]);

  // When any HeaderButton has a Type property, render them ourselves using
  // SmartConnect's Button component (which supports primary/secondary/tertiary)
  // instead of passing them to eone-ui's PanelHeader (which only supports
  // primary/secondary and always overwrites the Class prop).
  const hasTypedButtons = useMemo(
    () => props.HeaderButtons?.some((btn) => btn.Type !== undefined) ?? false,
    [props.HeaderButtons],
  );

  const customHeaderButtons = useMemo(() => {
    if (!hasTypedButtons || !props.HeaderButtons) return null;
    const visibleButtons = props.HeaderButtons.filter((btn) => btn.Visible !== false);
    if (visibleButtons.length === 0) return null;
    return (
      <div className="sce-custom-header-buttons">
        {visibleButtons.map((btn, index) => (
          <Button
            key={index}
            Label={btn.Label}
            Icon={btn.Icon}
            Type={btn.Type ?? 'primary'}
            OnClick={btn.OnClick}
            MenuItems={btn.MenuItems}
          />
        ))}
      </div>
    );
  }, [hasTypedButtons, props.HeaderButtons]);

  return (
    <div
      className="sce-advance-table-auto-size sce-advance-table-interactive"
      style={{ position: 'relative', '--sc-button-count': visibleButtonCount } as any}
      ref={ref as any}
    >
      {customHeaderButtons}
      {size && (
        <>
          <SCAdvanceTable
            {...restProps}
            StickyHeader
            Columns={columnsWithDefaults}
            TableHeaderButtons={tableHeaderButtons}
            ShowSelectColumns={false}
            TableState={
              restProps.TableState ? { ...tableState, ...restProps.TableState } : tableState
            }
            Id={undefined}
            HeaderButtons={hasTypedButtons ? undefined : restProps.HeaderButtons}
            //BodyHeight={
            //	props.Title ||
            //		props.Search ||
            //		tableHeaderButtons.length > 0 ||
            //		(props.HeaderButtons && props.HeaderButtons.length > 0)
            //		? `calc(${size.height}px - 72px - 1px)`
            //		: `calc(${size.height}px)
            //}
          />
          {showColumnManager && tableState && (
            <div style={{ position: 'absolute', top: 40, right: 100, zIndex: 1000 }}>
              <ColumnManagerModal
                columns={props.Columns.filter((column) => column.Visible != false)}
                columnOrder={(tableState.ColumnOrder as string[]) || []}
                selectedColumns={(tableState.Columns as string[]) || []}
                onApply={handleColumnManagerApply}
                onReset={handleColumnManagerReset}
                onClose={() => setShowColumnManager(false)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

/** Wrapper that unconditionally calls the cache hook when Id is provided */
const SCAdvanceTableInteractiveWithCache = <D extends BasicRecord>(
  props: SCAdvanceTableInteractiveProps<D>,
) => {
  const tableState = useAdvanceTableStateCache(props.Id!, props.InitialTableState);
  return <SCAdvanceTableInteractiveInner {...props} tableState={tableState} />;
};

const SCAdvanceTableInteractive = <D extends BasicRecord>(
  props: SCAdvanceTableInteractiveProps<D>,
) => {
  if (props.Id) {
    return <SCAdvanceTableInteractiveWithCache {...props} />;
  }
  return <SCAdvanceTableInteractiveInner {...props} tableState={null} />;
};

export { SCAdvanceTableInteractive };
